import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { X, CheckCircle, AlertCircle, ArrowRight, ArrowLeft } from "lucide-react";
import CMYuvaFormPart1 from "./CMYuvaFormPart1";
import CMYuvaFormPart2 from "./CMYuvaFormPart2";
import CMYuvaFormPart3 from "./CMYuvaFormPart3";
import CMYuvaFormPart4 from "./CMYuvaFormPart4";
import NotEligiblePopup from "./NotEligiblePopup";
import "../../styles/CmYuvaForm.css"; 

const CmYuvaForm = ({ setFormActive }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [showNotEligible, setShowNotEligible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    trigger,
    reset,
    watch,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  const liveInUp = watch("liveInUp");
  const progress = (pageNumber / 4) * 100;

  /* ---------------- SUBMIT ---------------- */
  const onSubmit = async (data) => {
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const finalData = {
        activeNpaLoan: data["activeNpaLoan"] === "yes",
        bankName: data["bankName"],
        branchDistance: Number(data["branchDistance"]),
        branchName: data["branchName"],
        contactNumber: data["contactNumber"],
        district: data["district"],
        email: data["email"],
        fullName: data["fullName"],
        ifscCode: data["ifscCode"],
        isUpResident: data["liveInUp"] === "yes",
        availedFinancialBenefit: data["takenFinancialBenefits"] === "yes",
      };

      const response = await fetch(
        "https://api-stg.gigmediaapp.com/campaign/post-cmyuva-scheme",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalData),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error("Too many requests. Please try again later.");
        }
        throw new Error(errData?.message || "Something went wrong");
      }

      await response.json();
      setSuccess(true);
      reset();
    } catch (err) {
      setError(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- AUTO CLOSE ---------------- */
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setFormActive(false), 2500);
      return () => clearTimeout(t);
    }
  }, [success, setFormActive]);

  useEffect(() => {
    if (liveInUp === "no") setShowNotEligible(true);
  }, [liveInUp]);

  /* ---------------- ESCAPE TO CLOSE ---------------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setFormActive(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [setFormActive]);

  /* ---------------- NEXT ---------------- */
  const handleNext = async () => {
    const stepFields = {
      1: ["fullName", "email", "contactNumber"],
      2: ["activeNpaLoan", "takenFinancialBenefits"],
      3: ["liveInUp", "district"],
      4: ["bankName", "branchName", "ifscCode", "branchDistance"],
    };

    const valid = await trigger(stepFields[pageNumber]);
    if (valid && pageNumber < 4) setPageNumber((p) => p + 1);
  };

/* ---------------- BACK ---------------- */
  const handleBack = () => {
    if (pageNumber > 1) setPageNumber((p) => p - 1);
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div
      className="modal_overlay d-flex align-items-center justify-content-center p-3"
      onClick={() => setFormActive(false)}
    >
      {/* Not Eligible Popup */}
      {showNotEligible && (
        <NotEligiblePopup
          setShowNotEligible={setShowNotEligible}
          setFormActive={setFormActive}
        />
      )}

      {/* Main Modal Card */}
      <div
        className="backdrop-blur-md bg-white/[0.03] border border-white/10 rounded-4 shadow-lg w-100 overflow-hidden position-relative animate-up"
        style={{ maxWidth: "900px", minHeight: "500px" }}
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Close Button */}
        {!loading && !success && (
          <button
            className="btn btn-light rounded-circle position-absolute top-0 end-0 m-3 z-3 shadow-sm"
            onClick={() => setFormActive(false)}
            style={{ width: "40px", height: "40px", padding: 0 }}
          >
            <X size={20} />
          </button>
        )}

        {/* LOADING / SUCCESS / ERROR STATE */}
        {loading || success || error ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 p-5 text-center" style={{minHeight: '400px'}}>
            {loading && (
              <>
                <div className="spinner-border text-theme mb-3" role="status" style={{width: '3rem', height: '3rem'}}></div>
                <h4 className="fw-bold">Submitting Application...</h4>
                <p className="text-gray-300">Please wait while we process your details.</p>
              </>
            )}
            {success && (
              <>
                <CheckCircle size={64} className="text-success mb-3" />
                <h4 className="fw-bold text-success">Application Submitted!</h4>
                <p className="text-gray-300">Redirecting you shortly...</p>
              </>
            )}
            {error && (
              <>
                <AlertCircle size={64} className="text-danger mb-3" />
                <h4 className="fw-bold text-danger">Submission Failed</h4>
                <p className="text-gray-300 mb-4">{error}</p>
                <button className="btn btn-outline-danger px-4 rounded-pill" onClick={() => setError("")}>Try Again</button>
              </>
            )}
          </div>
        ) : (
          /* FORM CONTENT */
          <div className="d-flex flex-column h-100">
            
            {/* Progress Bar (Using bg-theme) */}
            <div className="progress" style={{ height: "6px" }}>
              <div
                className="progress-bar bg-theme"
                role="progressbar"
                style={{ width: `${progress}%`, transition: "width 0.4s ease" }}
              />
            </div>

            <div className="flex-grow-1">
              <form onSubmit={handleSubmit(onSubmit)} className="h-100">
                {pageNumber === 1 && <CMYuvaFormPart1 register={register} errors={errors} />}
                {pageNumber === 2 && <CMYuvaFormPart2 register={register} errors={errors} liveInUp={liveInUp} />}
                {pageNumber === 3 && <CMYuvaFormPart3 register={register} errors={errors} liveInUp={liveInUp} />}
                {pageNumber === 4 && <CMYuvaFormPart4 register={register} errors={errors} />}
              </form>
            </div>

            {/* Footer Navigation */}
            <div className="mt-auto p-3 p-md-4 border-top border-white/10 backdrop-blur-md bg-white/[0.03]">
              <div className="d-flex justify-content-between align-items-center">
                
                {/* Step Counter (Left Side) */}
                <div className="text-gray-300 small fw-semibold">
                  Step {pageNumber} of 4
                </div>

                {/* Buttons Group (Right Side) */}
                <div className="d-flex gap-2 gap-md-3">
                  
                  {/* Back Button - Hidden on Step 1 */}
                  {pageNumber > 1 && (
                    <button
                      type="button"
                      className="btn btn-theme rounded-pill px-3 px-sm-4 px-md-5 py-2 d-flex align-items-center shadow-sm"
                      onClick={handleBack}
                    >
                      <ArrowLeft size={18} className="me-0 me-md-2" />
                      <span className="d-none d-sm-inline">Back</span>
                    </button>
                  )}

                  {/* Next / Submit Button */}
                  <button
                    type="button"
                    className="btn btn-theme rounded-pill px-3 px-sm-4 px-md-5 py-2 d-flex align-items-center shadow-sm"
                    onClick={pageNumber === 4 ? handleSubmit(onSubmit) : handleNext}
                  >
                    <span className="me-0 me-md-2">
                      {pageNumber === 4 ? "Submit" : "Next"}
                    </span>
                    {pageNumber < 4 && <ArrowRight size={18} />}
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CmYuvaForm;
